import { switchMap, filter, debounceVar } from "@/fp";
import { Ref, watch, ref, computed } from "@vue/composition-api";
import { ListResult } from "../models";

export function UseSearch<T>(
  searchFunc: (q: string, cursor: string) => Promise<ListResult<T>>
) {
  const query = ref("");
  const isSearching = computed(() => query.value.length > 0);
  const results: Ref<T[]> = ref([]);
  const last_cursor = ref("");

  const isLoading = ref(false);
  const noResults = computed(
    () => results.value.length === 0 && !isLoading.value
  );

  const moreThen0Letters = filter<string, void>((q) => q.length > 0);

  const debounce250 = debounceVar(() => (query.value.length > 1 ? 250 : 0));

  const search = async (str: string) => {
    str = str.replace(/\"/g, '\\"');
    // show loader
    isLoading.value = true;
    // clear items and cursor
    results.value = [];
    last_cursor.value = "";
    // search
    const res = await searchFunc(str, "");
    // set the results
    results.value = res.objects;
    last_cursor.value = res.cursor;
    // hide loader
    isLoading.value = false;
  };

  const loadMore = async () => {
    isLoading.value = true;
    const { cursor, objects } = await searchFunc(
      query.value,
      last_cursor.value
    );
    last_cursor.value = cursor;
    results.value = [...results.value, ...objects];
    isLoading.value = false;
  };

  const searchAction = moreThen0Letters(debounce250(switchMap(search)));

  const clearSearch = () => {
    query.value = "";
    last_cursor.value = "";
    results.value = [];
  };

  watch(query, searchAction);

  let input_el: HTMLInputElement | null;

  const on_key_press = (ev: KeyboardEvent) => {
    console.log("listen_to_esc code,key", ev.code, ev.key);
    if (ev.key.toLowerCase() === "escape") {
      clearSearch();
      input_el?.blur();
    }
  };

  const on_focus_listener = () => {
    input_el && input_el.addEventListener("keydown", on_key_press);
  };

  const listen_to_esc = (el: HTMLInputElement) => {
    input_el = el;
    input_el.addEventListener("focusin", on_focus_listener);
  };

  const destroy = () => {
    if (input_el) {
      input_el.removeEventListener("focusin", on_focus_listener);
      input_el.removeEventListener("keypress", on_key_press);
      input_el = null;
    }
    clearSearch();
  };

  return {
    query,
    isSearching,
    results,
    isLoading,
    clearSearch,
    loadMore,
    noResults,
    listen_to_esc,
    destroy,
  };
}
